import {userInteraction} from "@eusoft/webapp-framework/services/UserInteraction";
import {forModel} from "@eusoft/webapp-jsx";
import {objectEditor, Popup, type ViewNode} from "@eusoft/webapp-ui";
import {Action, Content, contentInfo, Message, ObjectEditor} from "@eusoft/webapp-ui";
import {required} from "@eusoft/webapp-ui/editors/Validators";
import {formatText} from "@eusoft/webapp-ui/utils/Format";
import {navigatePage} from "../../actions/Common";
import {MarkDown} from "../../components/MarkDown";
import {UserSessionStatus} from "../../entities/Commands";
import {isNativeApp, nativeApp} from "../../services/NativePayment";
import {userSession} from "../../services/UserSession";
import type {IContextInfo} from "../../components/ContextView";
import "./LoginPage.scss";

interface ILoginData {

    username: string;
    password: string;
    rememberMe: boolean;
}

export interface ILoginArgs {
    action?: () => Promise<unknown>;
}

export class LoginPage extends Content<ILoginArgs> implements IContextInfo {

    protected _backAction: () => Promise<unknown>;

    constructor() {

        super();

        this.init(LoginPage, {
            title: "login",
            style: "panel",
            body: forModel(this, m => <>

                <ObjectEditor<ILoginData>
                    ref={m.loginEditor}
                    validationMode="manual"
                    style={["vertical"]}
                    inputField={{style: ["outlined"] }}
                    commitMode="manual"
                    builder={bld => <>

                        {bld.text(m => m.username, {
                            validators: [required],
                            editor: {
                                autocomplete: "username"
                            }
                        })}
                        {bld.text(m => m.password, {
                            validators: [required],
                            editor: {
                                type: "password",
                                autocomplete: "current-password"
                            }
                        })}
                        {bld.boolean(m => m.rememberMe, {
                        })}
                </>} />

                {m.message && <Message type="error">{m.message}</Message>}

                <div className ="actions"> 
                    <Action style="text" visible={false} name="sign-up" onExecuteAsync={() => navigatePage("sign-up")}>msg-sign-up</Action>
                    <Action name="login" onExecuteAsync={() => m.loginAsync()}>login</Action>
                </div>

                {isNativeApp() && <>
                    <div className="else">{formatText("or-else")}</div>

                    <div className="scan-msg">
                        <Action name="scan" style="text" onExecuteAsync={() => m.scanAsync()}>scan</Action>
                        {formatText("msg-scan-access-request")}
                    </div>
                </>}

                {!isNativeApp() && <>
                    <div className="more-info">
                        <div style="margin-top: 1rem; text-align: center;">
                            {formatText("msg-more-info")} 
                        </div>
                        <div>
                            <Action name="scan" style="text" onExecuteAsync={() => {
                                window.open("https://www.pay-app.it", "_blank");
                            }}>
                                {formatText("visit-site")}
                            </Action>
                        </div>
                    </div>
                </>}
  
            </>)
        });
    }

    protected override async onLoadAsync(args: ILoginArgs) {

        this._backAction = args?.action;
        return true;
    }

    protected async scanAsync() {

        const res = await nativeApp.scanCodeAsync();   
        console.log(res);
        if (res?.code && res.code.includes("access-request")) {
            const reqId = res.code.split("/").findLast(() => true);
            navigatePage("access-request", { reqId }, "replace");
        }
        else
            userInteraction.messageAsync(formatText("msg-invalid-code"), "error", 3000);
    }

    protected async resendOtpAsync () {
        if(await userSession.sendOtpAsync())
            userInteraction.messageAsync(formatText("msg-otp-sent"), "success", 3000);
    }
    
    async validateOtpAsync() {        
        const data = {
            otp: undefined as string
        }
        const editor = objectEditor(bld => <>
            {bld.text(a => a.otp, {
                label: "otp",
                validators: [required],
                editor: {
                }
            })}
        </>, {
            value: data,
            style: "vertical",
            commitMode: "manual-inplace",
            inputField: {
                style: "filled"
            },
        });


        const popup = new Popup({
            body: forModel(this, m => <>
                <Message type={"info"} >
                    <MarkDown>{formatText("msg-validate-otp",  userSession.active.user.email) as string}</MarkDown>
                </Message>
                {editor}
                <div>{formatText("msg-otp-not-received")}</div>
                <Action 
                  name="resend-otp" 
                  onExecuteAsync={() => this.resendOtpAsync()}
                  style="text"
                >
                    resend-otp
                </Action>
            </>),
            name: "validate-otp",
            title:  formatText("validate-otp"),
            actions: [{
                name: "cancel",
                text: "cancel",
            },{
                name: "ok",
                text: "validate",
                priority: "primary",
                executeAsync: async () => {
                    if (!await editor.commitAsync())
                        return;
                    return await userSession.validateOtpAsync(data.otp);                    
                }
            }]
        });

        const sendOtpRes = await userSession.sendOtpAsync();
        if(!sendOtpRes) {
            await userSession.closeAsync();
            return false;
        }
        const res = await popup.showAsync();
        return res === "ok";
    }
    
    protected async loginAsync() {

        if (!await this.loginEditor.commitAsync())
            return;

        this.message = undefined;

        const result = await userSession.openAsync({
            username: this.loginEditor.value.username,
            password: this.loginEditor.value.password,
        }, this.loginEditor.value.rememberMe);
        
        if( result?.sessionStatus === UserSessionStatus.Pending){
            if(!await this.validateOtpAsync()) {
                this.message = formatText("err-otp-verification-failed");
                return;
            }
        }
        
        if (!result?.accessToken) {
            this.message = formatText("err-invalid-login");
        } else {
            if (this._backAction)
                this._backAction();
            else
                this.host.closeAsync(true);
        }

    }

    message: ViewNode;

    loginEditor: ObjectEditor<ILoginData>;
    
    hideContext = true;

    result: boolean;

    hideBottomNav = true;

    static override info = contentInfo({
        route: "/login",
        name: "login",
        factory: () => new LoginPage()
    })
}